import React, { PropsWithChildren } from "react";
import {
    AriaDisclosureProps,
    VisuallyHidden,
    mergeProps,
    useButton,
    useDisclosure,
    useFocusRing,
    useHover,
    useId,
} from "react-aria";
import {
    DisclosureGroupState,
    useDisclosureGroupState,
    useDisclosureState,
} from "react-stately";

import { ThreeUpBlock, Image3UpHoverBlock as Value } from "@reactivated";

import { ButtonPrimary } from "../Clickables";
import { MediaChooserBlock } from "../MediaChooserBlock";
import { RichText } from "../RichText";
import { Svg } from "../Svg";

import styles from "./index.module.scss";

const DisclosureGroupStateContext =
    React.createContext<DisclosureGroupState | null>(null);

interface DisclosureItemProps extends AriaDisclosureProps, ThreeUpBlock {
    id: string;
}

const DisclosureItem = ({
    media,
    title,
    subtitle,
    ...props
}: PropsWithChildren<DisclosureItemProps>) => {
    const defaultId = useId();
    const id = props.id || defaultId;
    const groupState = React.useContext(DisclosureGroupStateContext);
    const isExpanded = groupState
        ? groupState.expandedKeys.has(id)
        : props.isExpanded;
    const state = useDisclosureState({
        ...props,
        isExpanded,
        onExpandedChange(isExpanded) {
            if (groupState) groupState.toggleKey(id);
            props.onExpandedChange?.(isExpanded);
        },
    });

    const { hoverProps } = useHover({
        onHoverStart: (e) => {
            e.target.click();
        },
    });

    const panelRef = React.useRef<HTMLDivElement | null>(null);
    const triggerRef = React.useRef<HTMLButtonElement | null>(null);
    const isDisabled = props.isDisabled || groupState?.isDisabled || false;
    const { buttonProps: triggerProps, panelProps } = useDisclosure(
        {
            ...props,
            isExpanded,
            isDisabled,
        },
        state,
        panelRef,
    );
    const { buttonProps } = useButton(triggerProps, triggerRef);
    const { isFocusVisible, focusProps } = useFocusRing();
    const { hidden, ...panelPropsWithoutHidden } = panelProps;

    if (!media) return <></>;

    return (
        <div
            className={
                state.isExpanded ? styles.blockExpanded : styles.blockClosed
            }
        >
            <button
                ref={triggerRef}
                {...mergeProps(buttonProps, focusProps, hoverProps)}
                data-focus-visible={isFocusVisible ? "true" : undefined}
                className={styles.trigger}
            >
                <VisuallyHidden>{title}</VisuallyHidden>
            </button>
            <div
                className={styles.panel}
                ref={panelRef}
                {...panelPropsWithoutHidden}
            >
                <MediaChooserBlock
                    value={media}
                    wrapperAttrs={{ className: styles.mediaWrapper }}
                    videoControlAttrs={{ className: styles.videoControls }}
                />
                <div className={styles.content}>
                    <div className={styles.text}>
                        <div className={styles.textInner}>
                            <h3 className={styles.title}>{title}</h3>
                            {subtitle ? (
                                <p className={styles.subtitle}>{subtitle}</p>
                            ) : (
                                <span
                                    aria-hidden={true}
                                    className={styles.subtitle}
                                />
                            )}
                        </div>
                    </div>
                    <Svg name="arrow-in-square" className={styles.arrow} />
                </div>
            </div>
        </div>
    );
};

interface Props {
    value: Value;
    id?: string;
}

export const Image3UpHoverBlock = ({
    id: propId,
    value: { cta, header_content, items, theme },
}: Props) => {
    const state = useDisclosureGroupState({});
    const noneExpanded = state.expandedKeys.size === 0;

    if (!items || items.length === 0) return <></>;

    const id = propId || "three-up-hover-block";

    return (
        <section className={styles[theme ?? "match"]}>
            <div className={styles.header}>
                {header_content && (
                    <RichText
                        value={header_content}
                        className={styles.richText}
                    />
                )}
                {cta && cta.url && (
                    <div className={styles.headerCta}>
                        <ButtonPrimary simpleLink={cta} icon="arrow" />
                    </div>
                )}
            </div>

            <DisclosureGroupStateContext.Provider value={state}>
                <div
                    className={
                        noneExpanded
                            ? styles.innerNoneExpanded
                            : styles.innerExpanded
                    }
                    onMouseLeave={() => state.setExpandedKeys(new Set())}
                    onBlur={() => state.setExpandedKeys(new Set())}
                >
                    {items.map((item, index) => (
                        <DisclosureItem
                            {...item}
                            id={`${id}-${index}`}
                            key={`${id}-${index}`}
                        />
                    ))}
                </div>
            </DisclosureGroupStateContext.Provider>
        </section>
    );
};
